import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { landsService, tagsService, nestedTagsService, agentsService } from '../../services/api';
import { appSavedLandsService, consumerAuthService } from '../../services/appApi';
import colors from '../../theme';
import { getImageURL } from '../../utils/imageUtils';
import { filterLands } from './filterUtils';
import FilterPanel from './FilterPanel';
import MapView from './MapContainer';
import PropertiesList from './PropertiesList';
import DetailsPanel from './DetailsPanel';
import WhatsAppHandler from './WhatsAppHandler';
import TitleBanner from '../TitleBanner';
import DownloadAppBanner from '../DownloadAppBanner';

// Fix for default Leaflet markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Default map center (Kuwait)
const defaultCenter = [29.3759, 47.9774];

const DEFAULT_PAGE_TITLE = 'اراضي وشاليهات  للبيع في مدينة صباح الاحمدالبحرية';
const DEFAULT_META_DESCRIPTION =
  'منصة دروازة الصفاة العقارية  لعروض مدينة صباح الأحمد البحريةاراضي وشاليهات  للبيع في مدينة صباح الاحمدالبحرية المرحلة الخامسة والمرحلة الرابعة والمرحلة الثانية';
const DEFAULT_META_KEYWORDS =
  'اراضي للبيع, شاليهات للبيع, عقارات الكويت, مدينة صباح الاحمد البحرية, دروازة الصفاة';
const SITE_URL = 'https://ba7ria.com';
const DEFAULT_OG_IMAGE = `${SITE_URL}/logo512.png`;
// Static prefix used to build SEO-friendly tag-filter URLs, e.g.
// /صباح-الأحمد-البحرية-المرحلة-الأولى
const SITE_NAME_SLUG = 'صباح-الأحمد-البحرية';

// Turns a land's title into a URL-safe slug (keeps Arabic characters, strips
// anything that isn't a letter/number/space/hyphen, collapses spaces to '-').
const slugify = (text) => {
  if (!text) return '';
  return text
    .trim()
    .replace(/[^\p{L}\p{N}\s-]/gu, '')
    .replace(/\s+/g, '-');
};

const setMetaDescription = (content) => {
  const tag = document.querySelector('meta[name="description"]');
  if (tag) tag.setAttribute('content', content);
};

const setMetaKeywords = (content) => {
  const tag = document.querySelector('meta[name="keywords"]');
  if (tag) tag.setAttribute('content', content);
};

const setOgTag = (property, content) => {
  const tag = document.querySelector(`meta[property="${property}"]`);
  if (tag) tag.setAttribute('content', content);
};

// Updates the og:* tags (link-preview cards on WhatsApp/Facebook/etc. read
// these) to match whatever's currently shown — the site default, or a
// specific land once its details are open.
const setOgTags = ({ title, description, image, url }) => {
  setOgTag('og:title', title);
  setOgTag('og:description', description);
  setOgTag('og:image', image);
  setOgTag('og:url', url);
};

const LAND_STRUCTURED_DATA_ID = 'land-structured-data';

// Injects/removes a RealEstateListing JSON-LD block for the currently open
// land — helps Google show rich results (price, image) for listing pages.
const setLandStructuredData = (land, landUrl) => {
  let script = document.getElementById(LAND_STRUCTURED_DATA_ID);
  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = LAND_STRUCTURED_DATA_ID;
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'RealEstateListing',
    name: land.title,
    description: land.notes || DEFAULT_META_DESCRIPTION,
    url: landUrl,
    image: land.picture_url ? getImageURL(land.picture_url) : DEFAULT_OG_IMAGE,
    ...(land.price ? { offers: { '@type': 'Offer', price: land.price, priceCurrency: 'KWD' } } : {}),
  });
};

const clearLandStructuredData = () => {
  const script = document.getElementById(LAND_STRUCTURED_DATA_ID);
  if (script) script.remove();
};

// Builds a comma-separated keyword list from a land's tags, falling back to
// the site-wide defaults when the land has no tags.
const buildLandKeywords = (land) => {
  const tagNames = (land.tags || []).map((tag) => tag.title).filter(Boolean);
  return tagNames.length > 0 ? `${tagNames.join(', ')}, ${DEFAULT_META_KEYWORDS}` : DEFAULT_META_KEYWORDS;
};

const buildLandUrl = (land) => `${SITE_URL}/${land.id}/${slugify(land.title) || 'property'}`;

const MapDetailsPage = ({ 
  language = 'ar', 
  onFilterToggle, 
  filterOpen: parentFilterOpen, 
  onActiveFiltersChange, 
  searchQuery: headerSearchQuery, 
  onSearchChange,
  viewMode = 'map',
  selectedTags: parentSelectedTags = [],
  onTagsChange,
  isAuthenticated = false,
  onLandsChange,
  showSavedOnly = false,
  priceMin = null,
  priceMax = null
}) => {
  const navigate = useNavigate();
  const params = useParams();
  const isMobile = window.innerWidth < 768;

  // State
  const [lands, setLands] = useState([]);
  const [tags, setTags] = useState([]);
  const [nestedTags, setNestedTags] = useState([]);
  const [agents, setAgents] = useState([]);
  const [selectedTag, setSelectedTag] = useState(null);
  const [selectedLand, setSelectedLand] = useState(null);
  const [center, setCenter] = useState(defaultCenter);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [landTypes, setLandTypes] = useState([]);
  const [selectedLandType, setSelectedLandType] = useState(null);
  const [savedLandIds, setSavedLandIds] = useState([]);

  // Use parent's selectedTags
  const selectedTags = parentSelectedTags;

  const effectiveSearchQuery = headerSearchQuery || '';

  // Translation function
  const t = (arabicText, englishText) => {
    return language === 'ar' ? arabicText : englishText;
  };

  // Fetch data
  useEffect(() => {
    fetchTags();
    fetchNestedTags();
    fetchLandTypes();
    fetchAgents();
    fetchLands();
    if (consumerAuthService.isSignedIn()) {
      // app_saved_lands is the only saved-lands storage now, for staff and
      // consumer accounts alike — a staff login links an app_users row by
      // email and stores its token under the same consumerToken key (see
      // authService.login/handleGoogleSuccess), so this one check covers
      // both. Also means "saved" is the same list on web and mobile.
      fetchAppSavedLands();
    } else {
      // Guests with no account: save to localStorage
      const localSavedLands = localStorage.getItem('savedLands');
      if (localSavedLands) {
        try {
          const savedIds = JSON.parse(localSavedLands);
          setSavedLandIds(savedIds);
        } catch (error) {
          console.error('Error parsing saved lands from localStorage:', error);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]); // Re-fetch when authentication status changes

  // Update parent component about active filter count
  useEffect(() => {
    if (onActiveFiltersChange) {
      const filterCount = selectedTags.length + (selectedTag ? 1 : 0) + (effectiveSearchQuery ? 1 : 0);
      onActiveFiltersChange(filterCount);
    }
  }, [selectedTags, selectedTag, effectiveSearchQuery, onActiveFiltersChange]);

  // Deep-link support: if the app loads directly at /:landId/:titleSlug
  // (shared link, refresh), open that land's details once it's fetched.
  useEffect(() => {
    if (params.landId && lands.length > 0 && !selectedLand) {
      const land = lands.find(l => String(l.id) === String(params.landId));
      if (land) {
        setSelectedLand(land);
        if (land.latitude && land.longitude) {
          setCenter([parseFloat(land.latitude), parseFloat(land.longitude)]);
        }
        document.title = land.title || DEFAULT_PAGE_TITLE;
        setMetaDescription(land.notes || DEFAULT_META_DESCRIPTION);
        setMetaKeywords(buildLandKeywords(land));
        const landUrl = buildLandUrl(land);
        setOgTags({
          title: land.title || DEFAULT_PAGE_TITLE,
          description: land.notes || DEFAULT_META_DESCRIPTION,
          image: land.picture_url ? getImageURL(land.picture_url) : DEFAULT_OG_IMAGE,
          url: landUrl,
        });
        setLandStructuredData(land, landUrl);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.landId, lands]);

  // Deep-link support: /صباح-الأحمد-البحرية-<tag-name> opens with that tag's
  // filter already applied (shared link, refresh).
  useEffect(() => {
    if (params.tagSlug && tags.length > 0 && !selectedTag) {
      const prefix = `${SITE_NAME_SLUG}-`;
      const rawSlug = params.tagSlug.startsWith(prefix) ? params.tagSlug.slice(prefix.length) : params.tagSlug;
      const matchedTag = tags.find(tag => slugify(tag.title) === rawSlug);
      if (matchedTag) {
        setSelectedTag(matchedTag);
        document.title = `${matchedTag.title} - ${DEFAULT_PAGE_TITLE}`;
        setMetaKeywords(`${matchedTag.title}, ${DEFAULT_META_KEYWORDS}`);
        setOgTags({
          title: `${matchedTag.title} - ${DEFAULT_PAGE_TITLE}`,
          description: DEFAULT_META_DESCRIPTION,
          image: DEFAULT_OG_IMAGE,
          url: `${SITE_URL}/${SITE_NAME_SLUG}-${slugify(matchedTag.title) || matchedTag.id}`,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.tagSlug, tags]);

  // Update parent component with lands data
  useEffect(() => {
    console.log('MapView - Sending lands to parent:', lands.length);
    if (onLandsChange) {
      onLandsChange(lands);
    }
  }, [lands, onLandsChange]);

  // Filter lands — computed directly (useMemo) rather than mirrored into
  // its own state via an effect, which cost an extra render on every
  // keystroke in search/price filters (one for the input's own state, a
  // second right after when the effect ran); same fix as the admin Lands
  // tab's filteredLands.
  const filteredLands = useMemo(() => {
    let filtered = filterLands(lands, {
      selectedTag,
      selectedTags,
      selectedLandType,
      searchQuery: effectiveSearchQuery,
      priceMin,
      priceMax
    });

    // Apply saved lands filter if enabled
    if (showSavedOnly) {
      filtered = filtered.filter(land => savedLandIds.includes(land.id));
    }

    return filtered;
  }, [lands, selectedTag, selectedTags, selectedLandType, effectiveSearchQuery, showSavedOnly, savedLandIds, priceMin, priceMax]);

  const fetchTags = async () => {
    try {
      const fetchedTags = await tagsService.getAllTags();
      setTags(fetchedTags);
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  const fetchNestedTags = async () => {
    try {
      const fetchedNestedTags = await nestedTagsService.getAll();
      setNestedTags(fetchedNestedTags);
    } catch (error) {
      console.error('Error fetching nested tags:', error);
    }
  };

  const fetchLandTypes = async () => {
    try {
      const response = await fetch('/api/land-types');
      if (response.ok) {
        const types = await response.json();
        setLandTypes(types);
      }
    } catch (error) {
      console.error('Error fetching land types:', error);
    }
  };

  const fetchAgents = async () => {
    try {
      const data = await agentsService.getAllAgents();
      setAgents(data);
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  const fetchLands = async () => {
    try {
      setLoading(true);
      const data = await landsService.getAllLands();
      
      // Filter by land type based on authentication. Guests see lands and
      // chalets (matching mobile's GUEST_VISIBLE_TYPES) — staff-only chalet
      // fields are already redacted server-side in landsController.js.
      const GUEST_VISIBLE_TYPES = ['land', 'chalet'];
      const filteredData = isAuthenticated
        ? data // Show all types if authenticated
        : data.filter(land => !land.land_type_info || GUEST_VISIBLE_TYPES.includes(land.land_type_info.name));
      
      setLands(filteredData);
      if (filteredData.length > 0 && filteredData[0].latitude && filteredData[0].longitude) {
        setCenter([parseFloat(filteredData[0].latitude), parseFloat(filteredData[0].longitude)]);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAppSavedLands = async () => {
    try {
      const savedIds = await appSavedLandsService.getSavedLandIds();
      setSavedLandIds(savedIds);
    } catch (error) {
      console.error('Error fetching app saved lands:', error);
    }
  };

  const handleToggleSave = async (landId) => {
    try {
      const isSaved = savedLandIds.includes(landId);

      if (consumerAuthService.isSignedIn()) {
        // Staff or a signed-in consumer account (both now share the same
        // app_saved_lands identity — see the fetch effect above).
        if (isSaved) {
          await appSavedLandsService.unsaveLand(landId);
          setSavedLandIds(prev => prev.filter(id => id !== landId));
        } else {
          await appSavedLandsService.saveLand(landId);
          setSavedLandIds(prev => [...prev, landId]);
        }
      } else {
        // Guests with no account: save to localStorage
        let newSavedIds;
        if (isSaved) {
          newSavedIds = savedLandIds.filter(id => id !== landId);
        } else {
          newSavedIds = [...savedLandIds, landId];
        }
        setSavedLandIds(newSavedIds);
        localStorage.setItem('savedLands', JSON.stringify(newSavedIds));
      }
    } catch (error) {
      console.error('Error toggling save:', error);
    }
  };

  const handleTagFilter = (tag) => {
    setSelectedTag(tag);
    clearLandStructuredData();
    if (tag) {
      const tagSlug = slugify(tag.title) || tag.id;
      navigate(`/${SITE_NAME_SLUG}-${tagSlug}`);
      document.title = `${tag.title} - ${DEFAULT_PAGE_TITLE}`;
      setMetaKeywords(`${tag.title}, ${DEFAULT_META_KEYWORDS}`);
      setOgTags({
        title: `${tag.title} - ${DEFAULT_PAGE_TITLE}`,
        description: DEFAULT_META_DESCRIPTION,
        image: DEFAULT_OG_IMAGE,
        url: `${SITE_URL}/${SITE_NAME_SLUG}-${tagSlug}`,
      });
    } else {
      navigate('/');
      document.title = DEFAULT_PAGE_TITLE;
      setMetaKeywords(DEFAULT_META_KEYWORDS);
      setOgTags({
        title: DEFAULT_PAGE_TITLE,
        description: DEFAULT_META_DESCRIPTION,
        image: DEFAULT_OG_IMAGE,
        url: `${SITE_URL}/`,
      });
    }
  };

  // Tag chip tapped from inside the details view (e.g. DetailsPanel) — close
  // the details panel first so the newly-filtered results are visible.
  const handleDetailsTagClick = (tag) => {
    setSelectedLand(null);
    handleTagFilter(tag);
  };

  const handleLandTypeFilter = (landType) => {
    setSelectedLandType(landType);
  };

  const handleTagSelect = (tagTitle) => {
    if (!onTagsChange) return;
    
    const newSelectedTags = selectedTags.includes(tagTitle)
      ? selectedTags.filter(t => t !== tagTitle)
      : [...selectedTags, tagTitle];
    
    onTagsChange(newSelectedTags);
  };

  const getMarkerIcon = (land) => {
    const isPublished = land.published === 1 || land.published === true;
    
    // Priority: unpublished > tag color > land type color > default
    let color;
    if (!isPublished) {
      color = 'gray';
    } else if (land.tags && land.tags.length > 0) {
      color = land.tags[0].color;
    } else if (land.land_type_info) {
      // Use property type colors
      const typeColors = {
        'land': '#8B4513',
        'house': '#228B22',
        'building': '#4682B4',
        'apartment': '#32CD32',
        'villa': '#FF6347',
        'commercial': '#FF8C00',
        'chalet': '#20B2AA'
      };
      color = typeColors[land.land_type_info.name] || colors.primaryBrown;
    } else {
      color = colors.primaryBrown;
    }
    
    const landNumber = (land.land_number || land.code_number || land.id).toString();
    const displayNumber = landNumber.length > 4 ? landNumber.substring(0, 4) : landNumber;
    
    return L.divIcon({
      html: `
        <div class="custom-marker-wrapper" style="position: relative; width: 44px; height: 44px; display: flex; align-items: center; justify-content: center;">
          <div class="marker-circle" style="
            background-color: ${color};
            width: 30px;
            height: 30px;
            border-radius: 50%;
            border: 3px solid white;
            box-shadow: 0 2px 8px rgba(0,0,0,0.3);
            display: flex;
            align-items: center;
            justify-content: center;
            transition: transform 0.15s ease, width 0.2s ease, height 0.2s ease, margin 0.2s ease;
            cursor: pointer;
          ">
            <span style="
              color: white;
              font-size: 0px;
              font-weight: bold;
              text-shadow: 0 1px 2px rgba(0,0,0,0.3);
              transition: all 0.2s ease;
              opacity: 0;
            ">${displayNumber}</span>
          </div>
        </div>
        <style>
          .custom-marker-wrapper:hover .marker-circle {
            width: 50px !important;
            height: 50px !important;
          }
          .custom-marker-wrapper:hover .marker-circle span {
            font-size: 14px !important;
            opacity: 1 !important;
          }
          .custom-marker-wrapper:active .marker-circle {
            transform: scale(0.85);
          }
        </style>
      `,
      className: 'custom-marker',
      iconSize: [44, 44],
      iconAnchor: [22, 22]
    });
  };

  const getLandsByTag = (tagId, childTagIds) => {
    const matchIds = childTagIds && childTagIds.length > 0 ? [tagId, ...childTagIds] : [tagId];
    return lands.filter(land => land.tags && land.tags.some(tag => matchIds.includes(tag.id))).length;
  };

  const openDetailDialog = (land) => {
    setSelectedLand(land);
    if (land.latitude && land.longitude) {
      setCenter([parseFloat(land.latitude), parseFloat(land.longitude)]);
    }
    const titleSlug = slugify(land.title) || 'property';
    navigate(`/${land.id}/${titleSlug}`);
    document.title = land.title || DEFAULT_PAGE_TITLE;
    setMetaDescription(land.notes || DEFAULT_META_DESCRIPTION);
    setMetaKeywords(buildLandKeywords(land));
    const landUrl = buildLandUrl(land);
    setOgTags({
      title: land.title || DEFAULT_PAGE_TITLE,
      description: land.notes || DEFAULT_META_DESCRIPTION,
      image: land.picture_url ? getImageURL(land.picture_url) : DEFAULT_OG_IMAGE,
      url: landUrl,
    });
    setLandStructuredData(land, landUrl);
  };

  const closeDetailDialog = () => {
    setSelectedLand(null);
    navigate('/');
    document.title = DEFAULT_PAGE_TITLE;
    setMetaDescription(DEFAULT_META_DESCRIPTION);
    setMetaKeywords(DEFAULT_META_KEYWORDS);
    setOgTags({
      title: DEFAULT_PAGE_TITLE,
      description: DEFAULT_META_DESCRIPTION,
      image: DEFAULT_OG_IMAGE,
      url: `${SITE_URL}/`,
    });
    clearLandStructuredData();
  };

  const clearFilters = () => {
    setSelectedTag(null);
    setSelectedLandType(null);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US').format(price) + ' ' + t('د.ك', 'KD');
  };

  const formatCoordinates = (lat, lng) => {
    return `${parseFloat(lat).toFixed(6)}, ${parseFloat(lng).toFixed(6)}`;
  };

  const generateGoogleMapsLink = (lat, lng) => {
    return `https://www.google.com/maps?q=${lat},${lng}`;
  };

  const generateGalleryLink = (land) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/gallery/${land.id}`;
  };

  const openGoogleMaps = (lat, lng) => {
    window.open(generateGoogleMapsLink(lat, lng), '_blank');
  };

  const openKuwaitFinder = (link) => {
    window.open(link, '_blank');
  };

  const openAdminEdit = (codeNumber) => {
    // /map-admin was never a real route — AdminPanel reads ?editCode=
    // on mount and opens the edit form for the matching land once loaded.
    navigate(`/admin?editCode=${codeNumber}`);
  };

  const openImageGallery = (land) => {
    navigate(`/gallery/${land.id}`);
  };

  const downloadSinglePicture = (pictureUrl, land) => {
    window.open(getImageURL(pictureUrl), '_blank');
  };

  // WhatsApp Handler
  const whatsappHandler = WhatsAppHandler({
    agents,
    selectedTag,
    selectedTags,
    lands: filteredLands,
    formatPrice,
    generateGalleryLink,
    generateGoogleMapsLink,
    t,
    language,
    isMobile,
    showSavedOnly,
    savedLandIds,
    isStaff: isAuthenticated
  });

  // Listen for postMessage events from PACI map iframe
  useEffect(() => {
    const handleMessage = (event) => {
      console.log('[MapView] Received postMessage:', event.data);
      
      if (event.data.type === 'OPEN_WHATSAPP_DIALOG') {
        console.log('[MapView] Opening WhatsApp dialog from PACI map');
        whatsappHandler.openWhatsAppDialog();
      } else if (event.data.type === 'SHARE_BULK_WHATSAPP') {
        console.log('[MapView] Triggering bulk share from PACI map');
        whatsappHandler.shareBulkOnWhatsApp();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [whatsappHandler]);

  const toggleFilterDrawer = () => {
    setFilterDrawerOpen(!filterDrawerOpen);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('جاري التحميل...', 'Loading...')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <p className="text-red-600 font-medium mb-2">{t('خطأ', 'Error')}</p>
          <p className="text-red-500 text-sm">{error}</p>
          <button 
            onClick={fetchLands}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            {t('إعادة المحاولة', 'Retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <TitleBanner text={t('أراضي للبيع في مدينة صباح الأحمد البحرية', 'Lands for sale in Sabah Al-Ahmad Sea City')} />
      <div className="flex-1 min-h-0 flex flex-col md:grid md:grid-cols-4 md:gap-4 p-4">
      {/* Filter Panel - Desktop Sidebar */}
      <div className="hidden md:block md:col-span-1 h-full overflow-hidden rounded-xl shadow-lg">
        <FilterPanel
          tags={tags}
          nestedTags={nestedTags}
          lands={filteredLands}
          selectedTag={selectedTag}
          onTagFilter={handleTagFilter}
          onClearFilters={clearFilters}
          getLandsByTag={getLandsByTag}
          t={t}
          effectiveSearchQuery={effectiveSearchQuery}
          selectedTags={selectedTags}
          onTagSelect={handleTagSelect}
          landTypes={landTypes}
          selectedLandType={selectedLandType}
          onLandTypeFilter={handleLandTypeFilter}
          language={language}
          isAuthenticated={isAuthenticated}
        />
      </div>

      {/* Mobile Filter Drawer */}
      {filterDrawerOpen && (
        <div className="md:hidden fixed inset-0 z-50 bg-black/50" onClick={toggleFilterDrawer}>
          <div 
            className="absolute right-0 top-0 h-full w-80 max-w-[85vw] bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <FilterPanel
              tags={tags}
              nestedTags={nestedTags}
              lands={filteredLands}
              selectedTag={selectedTag}
              onTagFilter={handleTagFilter}
              onClearFilters={clearFilters}
              getLandsByTag={getLandsByTag}
              t={t}
              effectiveSearchQuery={effectiveSearchQuery}
              selectedTags={selectedTags}
              onTagSelect={handleTagSelect}
              landTypes={landTypes}
              selectedLandType={selectedLandType}
              onLandTypeFilter={handleLandTypeFilter}
              language={language}
              isAuthenticated={isAuthenticated}
            />
          </div>
        </div>
      )}

      {/* Map / Kuwait Finder / List — whichever view mode is active shows in
          this same content-area slot (see the 3-tab control in Header.jsx). */}
      <div className="flex-1 md:col-span-2 h-[60vh] md:h-full rounded-xl overflow-hidden shadow-lg">
        {viewMode === 'list' ? (
          <PropertiesList
            lands={filteredLands}
            onOpenDetail={openDetailDialog}
            formatPrice={formatPrice}
            t={t}
            language={language}
            savedLandIds={savedLandIds}
            onToggleSave={handleToggleSave}
          />
        ) : (
          <MapView
            showPaciMap={viewMode === 'paci'}
            filteredLands={filteredLands}
            language={language}
            onOpenDetail={openDetailDialog}
            center={center}
            getMarkerIcon={getMarkerIcon}
            formatPrice={formatPrice}
            t={t}
            isMobile={isMobile}
            fetchLands={fetchLands}
            WhatsAppButtons={whatsappHandler.WhatsAppButtons}
            tags={tags}
            nestedTags={nestedTags}
          />
        )}
      </div>

      {/* Details Panel — on mobile this presents as an app-style bottom
          sheet floating over the map (fixed, slides up, translucent so the
          map stays dimly visible through it, dismissed via backdrop tap or
          the panel's own X button); on desktop it's a normal grid column.
          z-[1100]/[1101] sit above Leaflet's own z-[1000] panes/controls
          (see DownloadAppBanner's note on that) but below the header's
          z-[1300], so the header stays visible and undimmed above it. */}
      {selectedLand && (
        <>
          <div
            className="md:hidden fixed inset-0 z-[1100] bg-black/20 animate-sheet-backdrop"
            onClick={closeDetailDialog}
          />
          <div className="fixed inset-x-0 bottom-0 z-[1101] h-[80vh] rounded-t-2xl shadow-2xl animate-sheet-up md:static md:inset-auto md:z-auto md:h-full md:col-span-1 md:rounded-xl md:shadow-lg md:animate-none overflow-hidden">
            <DetailsPanel
              selectedLand={selectedLand}
              onClose={closeDetailDialog}
              formatPrice={formatPrice}
              formatCoordinates={formatCoordinates}
              openImageGallery={openImageGallery}
              agents={agents}
              openWhatsAppDialog={() => whatsappHandler.openWhatsAppDialog(selectedLand)}
              shareOnWhatsApp={() => whatsappHandler.shareOnWhatsApp(selectedLand)}
              openGoogleMaps={openGoogleMaps}
              openKuwaitFinder={openKuwaitFinder}
              openAdminEdit={openAdminEdit}
              downloadSinglePicture={downloadSinglePicture}
              onOpenFullDetails={openDetailDialog}
              onToggleSave={handleToggleSave}
              isSaved={savedLandIds.includes(selectedLand.id)}
              isAuthenticated={isAuthenticated}
              onTagClick={handleDetailsTagClick}
              t={t}
              language={language}
            />
          </div>
        </>
      )}

      {/* WhatsApp Dialog */}
      <whatsappHandler.WhatsAppDialog />
      <whatsappHandler.SplitMessagesDialog />
      <whatsappHandler.WhatsAppPhoneModal />
      </div>

      <DownloadAppBanner language={language} />
    </div>
  );
};

export default MapDetailsPage;
